import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FactionFunding, FundingCampaignCount, FundingNamed, FundingTotals, PartyFundingDoc } from '../../types/types';
import { getPartyFunding } from '../../data/cache';
import { useBreakpoint } from '../../useBreakpoint';
import { countLabel } from '../../utils/format';
import Grid from '../../layout/Grid';
import { Card, CardHeader } from '../Card';
import Bar from '../Bar';
import Button from '../Button';
import Icon from '../Icon';
import { EmptyState, ErrorState } from '../States';
import { Skeleton, SkeletonText } from '../Skeleton';
import { color, font, radius, space, weight } from '../../theme';

/* The party page's מימון tab (owner, 2026-10-17: design A from the mockups, privacy B,
   the explainer as its own page at /parties/funding). Before the campaign's election
   results it shows the interim version: the monthly state funding, then the private money
   the party reported for the campaign, then its donations year by year (so a party that
   reported before and went quiet doesn't look like a gap in the data). Donors are never
   named; guarantors and lenders are. */

export const FUNDING_EXPLAINER_PATH = '/parties/funding';

// The factions' audited accounts for 2024 (Comptroller, Documents/2026/2026-Knesset-25-2024.pdf,
// תרשים 2): donations ₪2.6M, about 2% of their income; state funding ₪147.6M, 88%
export const DONATION_SHARE_NOTE = 'בכל הסיעות יחד, תרומות הן כ-2% מההכנסות ו-88% מגיעים מהמדינה (דוח מבקר המדינה על 2024).';

// "20.9 מיליון ₪" from a million up, "98,280 ₪" below
export const fmtMoney = (n: number): string => {
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(Math.abs(n) >= 1e7 ? 1 : 2).replace(/\.?0+$/, '')} מיליון ₪`;
  return `${Math.round(n).toLocaleString('he-IL')} ₪`;
};
const fmtExact = (n: number): string => `${Math.round(n).toLocaleString('he-IL')} ₪`;
// '2022-11-01' → '1.11.2022'
export const fmtDay = (d: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d || '');
  return m ? `${Number(m[3])}.${Number(m[2])}.${m[1]}` : '';
};

const ofCampaign = (t: FundingTotals, campaign: number): FundingCampaignCount => t.byCampaign[String(campaign)] || { count: 0, total: 0 };

// "תרומה אחת, 22.11.2022" / "1,766 תרומות, האחרונה מ-30.9.2026" (no date on docs written before the dates)
const campaignSub = (c: FundingCampaignCount, one: string, many: string, lastOf: string): string => {
  const label = countLabel(c.count, one, many);
  const last = fmtDay(c.last);
  if (!last) return label;
  return c.count === 1 ? `${label}, ${last}` : `${label}, ${lastOf} מ-${last}`;
};

const MoneyRow = ({ label, sub, value, muted = false }: { label: string; sub?: React.ReactNode; value: string; muted?: boolean }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.sm }}>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: font.label, fontWeight: weight.medium, color: color.text }}>{label}</div>
      {sub && <div style={{ fontSize: font.caption, color: color.textTertiary, marginTop: space.xxs, lineHeight: 1.45 }}>{sub}</div>}
    </div>
    <div style={{ fontSize: font.label, fontWeight: muted ? weight.regular : weight.bold, color: muted ? color.textSecondary : color.text, whiteSpace: 'nowrap' }}>{value}</div>
  </div>
);

// Guarantors or lenders, largest first: five, then all on request
const NamedList = ({ items, allLabel, loans = false }: { items: FundingNamed[]; allLabel: string; loans?: boolean }) => {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, 5);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
      <div style={{ background: color.surface, borderRadius: radius.md, overflow: 'hidden' }}>
        {shown.map((g, i) => (
          <div key={`${g.name}|${g.date}|${g.amount}|${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.sm, padding: `${space.sm + 2}px ${space.md}px`, borderTop: i > 0 ? `1px solid ${color.divider}` : 'none' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: font.small, fontWeight: weight.medium, color: color.text }}>{g.name}</div>
              <div style={{ fontSize: font.caption, color: color.textTertiary }}>
                {[g.city, fmtDay(g.date), loans && g.returned ? `הוחזרו ${fmtExact(g.returned)}` : ''].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div style={{ fontSize: font.small, fontWeight: weight.bold, color: color.text, whiteSpace: 'nowrap' }}>{fmtExact(g.amount)}</div>
          </div>
        ))}
      </div>
      {items.length > 5 && (
        <Button variant="ghost" onClick={() => setAll(!all)} style={{ alignSelf: 'flex-start', padding: `0 ${space.sm}px` }}>
          {all ? 'הצגת 5 הגדולים בלבד' : allLabel}
        </Button>
      )}
    </div>
  );
};

// The donations each year, as a bar per year (largest year = full bar)
const DonationYears = ({ totals }: { totals: FundingTotals }) => {
  const years = Object.keys(totals.byYear).sort();
  if (years.length === 0) return <div style={{ fontSize: font.small, color: color.textSecondary }}>לא נמצאו תרומות של המפלגה במאגר של מבקר המדינה.</div>;
  const max = Math.max(...years.map(y => totals.byYear[y].total), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
      {years.map(y => {
        const e = totals.byYear[y];
        return (
          <div key={y} style={{ display: 'flex', alignItems: 'center', gap: space.sm }}>
            <span style={{ width: 36, flexShrink: 0, fontSize: font.small, fontWeight: weight.medium, color: color.text }}>{y}</span>
            <Bar value={e.total} max={max} height={6} style={{ flex: 1, minWidth: 24 }} />
            <span style={{ fontSize: font.caption, color: color.textSecondary, whiteSpace: 'nowrap' }}>{e.count.toLocaleString('he-IL')} · {fmtMoney(e.total)}</span>
          </div>
        );
      })}
    </div>
  );
};

const subheading: React.CSSProperties = { fontSize: font.label, fontWeight: weight.bold, color: color.text, margin: 0 };
const section: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: space.md, borderTop: `1px solid ${color.divider}`, paddingTop: space.lg };

const ExplainerLink = ({ from }: { from: string }) => (
  <Link to={FUNDING_EXPLAINER_PATH} state={{ from }} style={{ display: 'inline-flex', alignItems: 'center', gap: space.xxs, fontSize: font.small, fontWeight: weight.medium, color: color.primary, textDecoration: 'none' }}>
    איך מפלגות ממומנות?<Icon name="chevronBack" size={14} strokeWidth={2.2} />
  </Link>
);

interface Props {
  partyId: string;
  partyName: string;
  // The Knesset the party sat in; an earlier Knesset's figures come from its archive
  knesset?: number;
}

export const PartyFundingTab = ({ partyId, partyName, knesset }: Props) => {
  const { isDesktop } = useBreakpoint();
  const [data, setData] = useState<PartyFundingDoc | null | undefined>(undefined);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    getPartyFunding(knesset)
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [knesset, reloadKey]);

  const wrap: React.CSSProperties = { padding: isDesktop ? 0 : `${space.md}px ${space.lg}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: space.md };

  if (error) return <div style={wrap}><Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card></div>;
  if (data === undefined) {
    return (
      <div data-skeleton="true" style={wrap}>
        <Card>
          <Skeleton width="35%" height={20} />
          <div style={{ height: space.lg }} />
          <Skeleton width="55%" height={34} />
          <div style={{ height: space.lg }} />
          <SkeletonText lines={4} />
        </Card>
      </div>
    );
  }
  const f: FactionFunding | undefined = data ? data.factions[partyId] : undefined;
  if (!data || !f) {
    return (
      <div style={wrap}>
        <Card padding={0}>
          <EmptyState icon="document" title="אין עדיין נתוני מימון לסיעה" body="נתוני המימון מתעדכנים מדיווחי המפלגות למבקר המדינה." action={<ExplainerLink from={partyName} />} />
        </Card>
      </div>
    );
  }

  const campaign = data.campaign;
  const since = data.electionDays[String(campaign - 1)];
  const mapped = f.parties.length > 0;
  // The party sat before this campaign's election, so its results (and the campaign's state funding) aren't in yet
  const resultsPending = (knesset ?? 0) < campaign;
  const donations = ofCampaign(f.donations, campaign);
  const guarantees = ofCampaign(f.guarantees, campaign);
  const loans = ofCampaign(f.loans, campaign);
  const guarantors = f.guarantors.filter(g => g.campaign === campaign);
  const lenders = f.lenders.filter(g => g.campaign === campaign);
  const perSeat = Math.round(data.unit * data.monthlyShare);

  const summary = (
    <Card>
      <CardHeader title="מימון המפלגה" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
        {/* The monthly funding: every month of the term, not the campaign */}
        {f.monthly !== null && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
            <h3 style={subheading}>מהמדינה, בכל חודש</h3>
            <div style={{ fontSize: 28, fontWeight: 800, color: color.text, lineHeight: 1.1 }}>{fmtMoney(f.monthly)}</div>
            <div style={{ fontSize: font.caption, color: color.textTertiary, lineHeight: 1.45 }}>
              לפי חוק מימון מפלגות: {fmtExact(perSeat)} {f.seats === 1 ? 'למנדט היחיד' : `לכל אחד מ-${f.seats} המנדטים`}, ועוד סכום זהה לסיעה
            </div>
          </div>
        )}

        {/* The campaign: the private money since the last election, then (after the results) its state funding */}
        <div style={f.monthly !== null ? section : { display: 'flex', flexDirection: 'column', gap: space.md }}>
          <div>
            <h3 style={subheading}>הקמפיין לכנסת ה-{campaign}</h3>
            {since && <div style={{ fontSize: font.caption, color: color.textTertiary, marginTop: space.xxs }}>מאז הבחירות לכנסת ה-{campaign - 1} ({fmtDay(since)})</div>}
          </div>
          {mapped ? (
            <>
              <MoneyRow
                label="תרומות"
                sub={donations.count > 0 ? campaignSub(donations, 'תרומה אחת', 'תרומות', 'האחרונה') : undefined}
                value={donations.count > 0 ? fmtMoney(donations.total) : 'לא דווחו'}
                muted={donations.count === 0}
              />
              <MoneyRow
                label="ערבויות"
                sub={guarantees.count > 0 ? `${campaignSub(guarantees, 'ערב אחד', 'ערבים', 'האחרון')}. פירוט בהמשך` : undefined}
                value={guarantees.count > 0 ? fmtMoney(guarantees.total) : 'לא דווחו'}
                muted={guarantees.count === 0}
              />
              <MoneyRow
                label="הלוואות"
                sub={loans.count > 0 ? campaignSub(loans, 'הלוואה אחת', 'הלוואות', 'האחרונה') : undefined}
                value={loans.count > 0 ? fmtMoney(loans.total) : 'לא דווחו'}
                muted={loans.count === 0}
              />
            </>
          ) : (
            <div style={{ fontSize: font.small, color: color.textSecondary, lineHeight: 1.5 }}>הדיווחים של המפלגה למבקר המדינה עוד לא מקושרים לסיעה הזו.</div>
          )}
          {resultsPending && (
            <div style={{ fontSize: font.small, color: color.primaryInk, background: color.primarySoft, borderRadius: radius.md, padding: `${space.sm + 2}px ${space.md}px`, lineHeight: 1.5 }}>
              מימון הקמפיין מהמדינה יחושב אחרי תוצאות הבחירות לכנסת ה-{campaign}.
            </div>
          )}
        </div>

        {/* Every year the party reported donations, so a quiet party reads as quiet, not as missing data */}
        {mapped && (
          <div style={section}>
            <h3 style={subheading}>תרומות שדווחו לאורך השנים</h3>
            <DonationYears totals={f.donations} />
            <div style={{ fontSize: font.caption, color: color.textTertiary, lineHeight: 1.5 }}>{DONATION_SHARE_NOTE}</div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <div style={{ fontSize: font.caption, color: color.textTertiary, lineHeight: 1.5 }}>
            מקור: דיווחי המפלגה למבקר המדינה{f.last ? `, האחרון מ-${fmtDay(f.last)}` : ''}. המימון הממלכתי מחושב לפי הנוסחה בחוק, לא לפי התשלומים בפועל.
          </div>
          <ExplainerLink from={partyName} />
        </div>
      </div>
    </Card>
  );

  const guaranteeCard = mapped && (
    <Card>
      <CardHeader title="מי ערב למפלגה" meta={guarantees.count > 0 ? `${guarantees.count} · ${fmtMoney(guarantees.total)}` : undefined} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
        <div style={{ fontSize: font.small, color: color.textSecondary, lineHeight: 1.5 }}>
          ערבות אינה כסף שניתן למפלגה: הערב מתחייב לשלם אם המפלגה לא תחזיר הלוואה או מקדמה מהמדינה.
        </div>
        {guarantors.length > 0
          ? <div style={{ background: color.surfaceMuted, borderRadius: radius.lg, padding: space.sm }}><NamedList items={guarantors} allLabel={`כל ${guarantors.length} הערבים`} /></div>
          : <div style={{ fontSize: font.small, color: color.textSecondary }}>לא דווחו ערבויות בקמפיין הזה.</div>}
        {lenders.length > 0 && (
          <>
            <h3 style={{ ...subheading, marginTop: space.xs }}>מי הלווה למפלגה</h3>
            <div style={{ background: color.surfaceMuted, borderRadius: radius.lg, padding: space.sm }}><NamedList items={lenders} allLabel={`כל ${lenders.length} המלווים`} loans /></div>
          </>
        )}
        <div style={{ fontSize: font.caption, color: color.textTertiary, lineHeight: 1.5 }}>
          שמות הערבים והמלווים כפי שפרסם מבקר המדינה. שמות התורמים לא מוצגים באתר.
        </div>
      </div>
    </Card>
  );

  return (
    <div style={wrap}>
      {/* Desktop: the two cards side by side; below it they stack */}
      <Grid onlyOnDesktop cols={{ desktop: 2 }} gap="12px" style={{ alignItems: 'start' }}>
        {summary}
        {guaranteeCard}
      </Grid>
    </div>
  );
};

export default PartyFundingTab;
